import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import useApi from '../../../hooks/useApi';
import apiService from '../../../services/apiService';
import Table from '../../../components/Table';
import Modal from '../../../components/Modal';
import FormInput from '../../../components/FormInput';
import { FaEdit, FaTrash, FaFilePdf, FaImage, FaTimes, FaEye, FaDownload } from 'react-icons/fa';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import Select from 'react-select';
import React from 'react';
const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

function Content() {
  const { data: content, loading: contentLoading, fetchData: fetchContent } = useApi('/content');
  const { data: categories, loading: categoriesLoading } = useApi('/content/categories');
  const [isContentModalOpen, setIsContentModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewType, setPreviewType] = useState('');
  const [editingContentId, setEditingContentId] = useState(null);
  const [contentForm, setContentForm] = useState({
    title: '',
    category: '',
    subcategory: '',
    type: '',
    contentUrl: '',
    description: '',
    previewImage: null,
    existingPreviewImage: null,
    files: [],
    existingFiles: [],
  });
  const [filePreviews, setFilePreviews] = useState([]);
  const [previewImagePreview, setPreviewImagePreview] = useState('');
  const [contentErrors, setContentErrors] = useState({});

  useEffect(() => {
    const filePreviews = contentForm.files.map(file => {
      if (file.type.startsWith('image/')) {
        return { url: URL.createObjectURL(file), type: 'image' };
      }
      return { url: 'pdf', type: 'pdf' };
    });
    const validExistingFiles = (contentForm.existingFiles || []).filter(file => 
      file?.url && typeof file.url === 'string' && file.url.startsWith('/uploads/')
    );
    const existingFilePreviews = validExistingFiles.map(file => ({
      url: `${BASE_URL}${file.url}`,
      type: file.type,
    }));
    setFilePreviews([...existingFilePreviews, ...filePreviews]);
    if (contentForm.previewImage) {
      setPreviewImagePreview(URL.createObjectURL(contentForm.previewImage));
    } else if (contentForm.existingPreviewImage?.url && typeof contentForm.existingPreviewImage.url === 'string' && contentForm.existingPreviewImage.url.startsWith('/uploads/')) {
      setPreviewImagePreview(`${BASE_URL}${contentForm.existingPreviewImage.url}`);
    } else {
      setPreviewImagePreview('');
    }
  }, [contentForm.files, contentForm.existingFiles, contentForm.previewImage, contentForm.existingPreviewImage]);

  const validateContentForm = () => {
    const errors = {};
    if (!contentForm.title.trim()) errors.title = 'Title is required';
    if (!contentForm.category) errors.category = 'Category is required';
    if (!contentForm.subcategory) errors.subcategory = 'Subcategory is required';
    if (!contentForm.type) errors.type = 'Type is required';
    if (!contentForm.contentUrl.trim()) errors.contentUrl = 'Content URL is required';
    if (!contentForm.description.trim()) errors.description = 'Description is required';
    if (!contentForm.previewImage && !contentForm.existingPreviewImage?.url) {
      errors.previewImage = 'Preview image is required';
    }
    setContentErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleContentInputChange = (e) => {
    const { name, value } = e.target;
    setContentForm({ ...contentForm, [name]: value });
    setContentErrors({ ...contentErrors, [name]: '' });
  };

  const handlePreviewImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.match(/^image\/(jpg|jpeg)$/)) {
        toast.error('Preview image must be JPG or JPEG');
        return;
      }
      setContentForm({ ...contentForm, previewImage: file, existingPreviewImage: null });
      setContentErrors({ ...contentErrors, previewImage: '' });
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => file.type.match(/^(image\/(png|jpg|jpeg)|application\/pdf)$/));
    if (validFiles.length !== files.length) {
      toast.error('Only PNG, JPG, JPEG, or PDF files are allowed');
    }
    if (contentForm.files.length + validFiles.length > 5) {
      toast.error('Maximum 5 additional files allowed');
      return;
    }
    setContentForm({ ...contentForm, files: [...contentForm.files, ...validFiles] });
  };

  const handleRemoveFile = (index, isExisting) => {
    if (isExisting) {
      setContentForm({
        ...contentForm,
        existingFiles: contentForm.existingFiles.filter((_, i) => i !== index),
      });
    } else {
      setContentForm({
        ...contentForm,
        files: contentForm.files.filter((_, i) => i !== index),
      });
    }
  };

  const handleRemovePreviewImage = () => {
    setContentForm({ ...contentForm, previewImage: null, existingPreviewImage: null });
    setPreviewImagePreview('');
  };

  const handleEditorChange = (event, editor) => {
    const data = editor.getData();
    setContentForm({ ...contentForm, description: data });
    setContentErrors({ ...contentErrors, description: '' });
  };

  const handleContentSubmit = async (e) => {
    e.preventDefault();
    if (!validateContentForm()) {
      toast.error('Please fix form errors');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('title', contentForm.title);
      formData.append('category', contentForm.category);
      formData.append('subcategory', contentForm.subcategory);
      formData.append('type', contentForm.type);
      formData.append('contentUrl', contentForm.contentUrl);
      formData.append('description', contentForm.description);
      if (contentForm.previewImage) {
        formData.append('previewImage', contentForm.previewImage);
      }
      contentForm.files.forEach(file => formData.append('files', file));
      const validExistingFiles = (contentForm.existingFiles || []).filter(file => 
        file?.url && typeof file.url === 'string' && file.url.startsWith('/uploads/')
      );
      formData.append('existingFiles', JSON.stringify(validExistingFiles));
      const validExistingPreviewImage = contentForm.existingPreviewImage?.url && 
        typeof contentForm.existingPreviewImage.url === 'string' && 
        contentForm.existingPreviewImage.url.startsWith('/uploads/')
        ? contentForm.existingPreviewImage
        : null;
      formData.append('existingPreviewImage', JSON.stringify(validExistingPreviewImage));

      if (editingContentId) {
        await apiService.request('put', `/content/${editingContentId}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success('Content updated successfully');
      } else {
        await apiService.upload('/content', formData);
        toast.success('Content added successfully');
      }
      fetchContent();
      setIsContentModalOpen(false);
      setContentForm({
        title: '',
        category: '',
        subcategory: '',
        type: '',
        contentUrl: '',
        description: '',
        previewImage: null,
        existingPreviewImage: null,
        files: [],
        existingFiles: [],
      });
      setEditingContentId(null);
      setFilePreviews([]);
      setPreviewImagePreview('');
      setContentErrors({});
    } catch (err) {
      // Error handled by apiService interceptor
    }
  };

  const handleEditContent = (item) => {
    const validFiles = (item.files || []).filter(file => 
      file?.url && typeof file.url === 'string' && file.url.startsWith('/uploads/')
    );
    const validPreviewImage = item.previewImage?.url && 
      typeof item.previewImage.url === 'string' && 
      item.previewImage.url.startsWith('/uploads/') 
      ? item.previewImage 
      : null;
    setContentForm({
      title: item.title || '',
      category: item.category._id || '',
      subcategory: item.subcategory || '',
      type: item.type || '',
      contentUrl: item.contentUrl || '',
      description: item.description || '',
      previewImage: null,
      existingPreviewImage: validPreviewImage,
      files: [],
      existingFiles: validFiles,
    });
    setEditingContentId(item._id);
    setIsContentModalOpen(true);
    setContentErrors({});
  };

  const handleDeleteContent = async (contentId) => {
    if (!window.confirm('Are you sure you want to delete this content?')) return;
    try {
      await apiService.delete(`/content/${contentId}`);
      toast.success('Content deleted successfully');
      fetchContent();
    } catch (err) {
      // Error handled by apiService interceptor
    }
  };

  const handlePreviewClick = (url, type) => {
    if (url && typeof url === 'string' && url.startsWith('/uploads/')) {
      const fullUrl = `${BASE_URL}${url}`;
      console.log("full  ur l *********************",fullUrl)
      setPreviewUrl(fullUrl);
      setPreviewType(type);
      setIsPreviewModalOpen(true);
    } else {
      toast.error('Invalid file URL');
      console.warn('Invalid preview URL:', url);
    }
  };

  const contentColumns = [
    {
      key: 'previewImage',
      label: 'Thumbnail',
      render: (item) => (
        item.previewImage?.url && typeof item.previewImage.url === 'string' && item.previewImage.url.startsWith('/uploads/') ? (
          <img
            src={`${BASE_URL}${item.previewImage.url}`}
            alt={item.title}
            className="w-12 h-12 object-cover rounded cursor-pointer"
            onClick={() => handlePreviewClick(item.previewImage.url, 'image')}
          />
        ) : (
          'N/A'
        )
      ),
    },
    { key: 'title', label: 'Title', render: (item) => item.title || 'N/A' },
    { key: 'category', label: 'Category', render: (item) => item.category?.name || 'N/A' },
    { key: 'subcategory', label: 'Subcategory', render: (item) => item.subcategory || 'N/A' },
    { 
      key: 'files', 
      label: 'Files', 
      render: (item) => (
        <div className="flex space-x-2">
          {item.files && item.files.length > 0 ? (
            item.files.map((file, index) => (
              file?.url && typeof file.url === 'string' && file.url.startsWith('/uploads/') ? (
                file.type === 'pdf' ? (
                  <div key={index} className="flex space-x-1">
                    <button
                      onClick={() => handlePreviewClick(file.url, 'pdf')}
                      className="text-blue-500 hover:text-blue-700"
                      title="View PDF"
                    >
                      <FaEye />
                    </button>
                    <a
                      href={`${BASE_URL}${file.url}`}
                      download
                      className="text-green-500 hover:text-green-700"
                      title="Download PDF"
                    >
                      <FaDownload />
                    </a>
                  </div>
                ) : (
                  <img
                    key={index}
                    src={`${BASE_URL}${file.url}`}
                    alt={item.title}
                    className="w-12 h-12 object-cover rounded cursor-pointer"
                    onClick={() => handlePreviewClick(file.url, 'image')}
                  />
                )
              ) : null
            )).filter(Boolean)
          ) : (
            'N/A'
        )}
        </div>
      ),
    },
  ];

  const contentActions = [
    {
      label: 'Edit',
      className: 'text-yellow-500 hover:text-yellow-600',
      icon: <FaEdit />,
      onClick: handleEditContent,
    },
    {
      label: 'Delete',
      className: 'text-red-500 hover:text-red-600',
      icon: <FaTrash />,
      onClick: (item) => handleDeleteContent(item._id),
    },
  ];

  const typeOptions = [
    { value: 'article', label: 'Article' },
    { value: 'video', label: 'Video' },
    { value: 'guide', label: 'Guide' },
  ];

  return (
    <div className="">
      <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-6">Content Management</h2>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-semibold dark:text-white">Content</h3>
        <button
          onClick={() => setIsContentModalOpen(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          Add Content
        </button>
      </div>
      {contentLoading ? (
        <div className="card text-center text-gray-600 dark:text-gray-300">Loading...</div>
      ) : !content || content.length === 0 ? (
        <div className="card text-center text-gray-600 dark:text-gray-300">No content found.</div>
      ) : (
        <div className="card">
          <Table columns={contentColumns} data={content || []} actions={contentActions} />
        </div>
      )}
      <Modal
        isOpen={isContentModalOpen}
        onClose={() => {
          setIsContentModalOpen(false);
          setContentForm({
            title: '',
            category: '',
            subcategory: '',
            type: '',
            contentUrl: '',
            description: '',
            previewImage: null,
            existingPreviewImage: null,
            files: [],
            existingFiles: [],
          });
          setEditingContentId(null);
          setFilePreviews([]);
          setPreviewImagePreview('');
          setContentErrors({});
        }}
        title={editingContentId ? 'Edit Content' : 'Add Content'}
        className="max-w-full lg:max-w-4xl mx-4"
      >
        <div className="max-h-[80vh] overflow-y-auto p-4">
          <form onSubmit={handleContentSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="lg:col-span-2">
                <FormInput
                  label="Title"
                  name="title"
                  value={contentForm.title}
                  onChange={handleContentInputChange}
                  required
                  placeholder="Enter content title"
                  error={contentErrors.title}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Category</label>
                <Select
                  options={categories?.map(cat => ({ value: cat._id, label: cat.name })) || []}
                  value={categories?.find(cat => cat._id === contentForm.category) ? { value: contentForm.category, label: categories.find(cat => cat._id === contentForm.category).name } : null}
                  onChange={(selected) => {
                    setContentForm({ ...contentForm, category: selected ? selected.value : '', subcategory: '' });
                    setContentErrors({ ...contentErrors, category: '' });
                  }}
                  placeholder="Select category"
                  isClearable
                  className="mt-1"
                  classNamePrefix="react-select"
                />
                {contentErrors.category && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{contentErrors.category}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Subcategory</label>
                <Select
                  options={categories?.find(cat => cat._id === contentForm.category)?.subcategories.map(sub => ({ value: sub, label: sub })) || []}
                  value={contentForm.subcategory ? { value: contentForm.subcategory, label: contentForm.subcategory } : null}
                  onChange={(selected) => {
                    setContentForm({ ...contentForm, subcategory: selected ? selected.value : '' });
                    setContentErrors({ ...contentErrors, subcategory: '' });
                  }}
                  placeholder="Select subcategory"
                  isClearable
                  className="mt-1"
                  classNamePrefix="react-select"
                />
                {contentErrors.subcategory && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{contentErrors.subcategory}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Type</label>
                <Select
                  options={typeOptions}
                  value={typeOptions.find(opt => opt.value === contentForm.type) || null}
                  onChange={(selected) => {
                    setContentForm({ ...contentForm, type: selected ? selected.value : '' });
                    setContentErrors({ ...contentErrors, type: '' });
                  }}
                  placeholder="Select type"
                  isClearable
                  className="mt-1"
                  classNamePrefix="react-select"
                />
                {contentErrors.type && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{contentErrors.type}</p>
                )}
              </div>
              <div className="lg:col-span-2">
                <FormInput
                  label="Content URL"
                  type="url"
                  name="contentUrl"
                  value={contentForm.contentUrl}
                  onChange={handleContentInputChange}
                  required
                  placeholder="https://example.com/content"
                  error={contentErrors.contentUrl}
                />
              </div>
              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
                <div className="mt-1 ckeditor-container">
                  <CKEditor
                    editor={ClassicEditor}
                    data={contentForm.description}
                    onChange={handleEditorChange}
                    config={{
                      toolbar: [
                        'heading',
                        '|',
                        'bold',
                        'italic',
                        'underline',
                        'link',
                        '|',
                        'bulletedList',
                        'numberedList',
                        '|',
                        'blockQuote',
                        '|',
                        'undo',
                        'redo',
                      ],
                      language: 'en',
                    }}
                  />
                </div>
                {contentErrors.description && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{contentErrors.description}</p>
                )}
              </div>
              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Preview Image (JPG/JPEG, required)
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-md hover:border-blue-500">
                  <div className="space-y-1 text-center">
                    <FaImage className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
                    <div className="flex text-sm text-gray-600 dark:text-gray-300">
                      <label
                        htmlFor="preview-image-upload"
                        className="relative cursor-pointer bg-white dark:bg-gray-700 rounded-md font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500"
                      >
                        <span>Upload preview image</span>
                        <input
                          id="preview-image-upload"
                          name="previewImage"
                          type="file"
                          accept="image/jpg,image/jpeg"
                          onChange={handlePreviewImageChange}
                          className="sr-only"
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">JPG or JPEG up to 5MB</p>
                  </div>
                </div>
                {contentErrors.previewImage && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{contentErrors.previewImage}</p>
                )}
                {previewImagePreview && (
                  <div className="mt-4 flex items-center">
                    <img
                      src={previewImagePreview}
                      alt="Preview Image"
                      className="w-32 h-32 object-cover rounded"
                    />
                    <button
                      type="button"
                      onClick={handleRemovePreviewImage}
                      className="ml-4 text-red-500 hover:text-red-700"
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Additional Files (Images or PDFs, max 5)
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-md hover:border-blue-500">
                  <div className="space-y-1 text-center">
                    <FaImage className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
                    <div className="flex text-sm text-gray-600 dark:text-gray-300">
                      <label
                        htmlFor="file-upload"
                        className="relative cursor-pointer bg-white dark:bg-gray-700 rounded-md font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500"
                      >
                        <span>Upload files</span>
                        <input
                          id="file-upload"
                          name="files"
                          type="file"
                          accept="image/png,image/jpg,image/jpeg,application/pdf"
                          multiple
                          onChange={handleFileChange}
                          className="sr-only"
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">PNG, JPG, JPEG, or PDF up to 5MB</p>
                  </div>
                </div>
                {filePreviews.length > 0 && (
                  <div className="mt-4 grid grid-cols-2 gap-4">
                    {filePreviews.map((preview, index) => (
                      <div key={index} className="flex items-center">
                        {preview.type === 'pdf' ? (
                          <div className="flex items-center space-x-2">
                            <FaFilePdf className="text-red-500 text-4xl" />
                            <span className="text-gray-700 dark:text-gray-300">
                              {contentForm.files[index]?.name || 'PDF File'}
                            </span>
                          </div>
                        ) : (
                          <img
                            src={preview.url}
                            alt="Preview"
                            className="w-32 h-32 object-cover rounded"
                          />
                        )}
                        <button
                          type="button"
                          onClick={() => handleRemoveFile(index, index < contentForm.existingFiles.length)}
                          className="ml-4 text-red-500 hover:text-red-700"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-6">
              <button
                type="button"
                onClick={() => {
                  setIsContentModalOpen(false);
                  setContentForm({
                    title: '',
                    category: '',
                    subcategory: '',
                    type: '',
                    contentUrl: '',
                    description: '',
                    previewImage: null,
                    existingPreviewImage: null,
                    files: [],
                    existingFiles: [],
                  });
                  setFilePreviews([]);
                  setPreviewImagePreview('');
                  setContentErrors({});
                }}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                disabled={contentLoading}
              >
                {editingContentId ? 'Update Content' : 'Add Content'}
              </button>
            </div>
          </form>
        </div>
      </Modal>
      <Modal
        isOpen={isPreviewModalOpen}
        onClose={() => {
          setIsPreviewModalOpen(false);
          setPreviewUrl('');
          setPreviewType('');
        }}
        title={previewType === 'image' ? 'Preview Image' : 'View PDF'}
        className="max-w-full lg:max-w-4xl mx-4"
      >
        <div className="p-4">
          {previewType === 'image' ? (
            <img
              src={previewUrl}
              alt="Large Preview"
              className="w-full max-h-[70vh] object-contain"
            />
          ) : (
            <div className="relative w-full h-[70vh]">
              <iframe
                src={`${previewUrl}#view=FitH&toolbar=1&navpanes=0`}
                title="PDF Preview"
                className="w-full h-full border-none"
                onError={(e) => console.error('Iframe error:', e)}
              />
              <a
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="absolute top-2 right-2 px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center text-sm"
              >
                Open in New Tab
              </a>
            </div>
          )}
          <div className="flex justify-end space-x-2 mt-4">
            {previewType === 'pdf' && (
              <a
                href={previewUrl}
                download
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"
              >
                <FaDownload className="mr-2" /> Download
              </a>
            )}
            <button
              onClick={() => {
                setIsPreviewModalOpen(false);
                setPreviewUrl('');
                setPreviewType('');
              }}
              className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

class ContentErrorBoundary extends React.Component {
  state = { hasError: false };

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error in Content component:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <h1>Something went wrong. Please try again.</h1>;
    }
    return this.props.children;
  }
}

export default function WrappedContent() {
  return (
    <ContentErrorBoundary>
      <Content />
    </ContentErrorBoundary>
  );
}